// frontend/src/pages/HowItWorks.js
// /how-it-works — premium redesign sharing the design system used by
// /for-businesses, /for-professionals and /freelancer (ForBusinesses.css).
// Covers the full platform: how clients book & buy, plus how salons and
// independent professionals run on OGESTYLE.

import React from "react";
import { Link } from "react-router-dom";
import CustomerHeader from "../components/CustomerHeader";
import CustomerFooter from "../components/CustomerFooter";
import {
  FaSearch,
  FaSlidersH,
  FaCalendarCheck,
  FaBell,
  FaCheckCircle,
  FaShoppingBag,
  FaClipboardCheck,
  FaCamera,
  FaArrowRight,
  FaUserFriends,
  FaUserTie,
  FaStoreAlt,
} from "react-icons/fa";
import "./ForBusinesses.css";
import "./HowItWorks.css";

const DEMO_URL = "https://demo.ogestyle.com";
const BOOKING_DEMO_URL = "https://www.ogestyle.com/demo";

const HERO_IMG = "/landing/looks.jpg";

const trust = [
  "Verified salons & professionals",
  "Real-time availability",
  "Secure payments by Stripe",
  "Automatic confirmations & reminders",
  "Shop products from every storefront",
];

const steps = [
  {
    n: "01",
    icon: <FaSearch />,
    title: "Find your match",
    desc: "Search salons, studios and independent pros near you — or browse the products each storefront sells.",
  },
  {
    n: "02",
    icon: <FaSlidersH />,
    title: "Compare & choose",
    desc: "Compare services, prices, ratings and live availability before you decide.",
  },
  {
    n: "03",
    icon: <FaCalendarCheck />,
    title: "Book — or buy",
    desc: "Reserve a time and pay securely in one checkout, or order products for in-shop pickup.",
  },
  {
    n: "04",
    icon: <FaBell />,
    title: "You're all set",
    desc: "Get instant confirmation, automatic reminders and easy rescheduling when plans change.",
  },
];

const serviceChecks = [
  "Real-time availability, no back-and-forth",
  "Clear prices before you book",
  "Instant confirmation & reminders",
  "Pay securely with Stripe",
];

const packageChecks = [
  "Buy a bundle of sessions in one payment",
  "Use each session when it suits you",
  "Remaining balance tracked automatically",
  "No expiry surprises — the terms are shown upfront",
];

const productChecks = [
  "Shop each salon or pro's own catalogue",
  "Pay online in a few taps",
  "Pay online & pick up in-shop",
  "Order updates & full history",
];

const platformFeatures = [
  { icon: <FaCalendarCheck />, text: "Online booking with real-time calendars" },
  { icon: <FaClipboardCheck />, text: "Packages with tracked session balances" },
  { icon: <FaShoppingBag />, text: "Product sales with in-shop pickup" },
  { icon: <FaCamera />, text: "Looks gallery of real results you can book from" },
  { icon: <FaStoreAlt />, text: "Storefronts for salons, pros & freelancers" },
  { icon: <FaBell />, text: "Automatic email & SMS reminders" },
  { icon: <FaUserTie />, text: "Earnings & reporting for professionals" },
];

const audience = [
  {
    icon: <FaUserFriends />,
    title: "For clients",
    desc: "Discover, book and buy from local beauty businesses — all in one place.",
    to: "/shops",
    cta: "Start booking",
  },
  {
    icon: <FaUserTie />,
    title: "For professionals",
    desc: "Work at a salon or launch your own independent storefront with your own dashboard.",
    to: "/for-professionals",
    cta: "Explore pro options",
  },
  {
    icon: <FaStoreAlt />,
    title: "For business owners",
    desc: "Run locations, your team, products, bookings and payouts from one calm dashboard.",
    to: "/for-businesses",
    cta: "See business tools",
  },
];

function HowItWorks() {
  return (
    <>
      <CustomerHeader />
      <main className="fb-page">
        {/* Hero */}
        <section className="fb-hero">
          <div className="fb-wrap fb-hero-grid">
            <div>
              <p className="fb-eyebrow">How OGESTYLE works</p>
              <h1 className="fb-display">
                Beauty, booked &amp; bought in one calm step
              </h1>
              <p className="fb-sub">
                OGESTYLE brings clients, salons, studios and independent
                professionals together. Find the right person, book a service
                in real time — or shop their product catalogue — all in one
                place.
              </p>
              <div className="fb-ctas">
                <Link className="fb-btn fb-btn-primary" to="/shops">
                  Start booking
                </Link>
                <a
                  className="fb-btn fb-btn-outline"
                  href={DEMO_URL}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  See the live demo
                </a>
              </div>
            </div>

            <div className="fb-hero-art">
              <span className="fb-blob" />
              <img
                className="fb-hero-img"
                src={HERO_IMG}
                alt="A finished hairstyle from an OGESTYLE professional's work"
              />
              <div className="fb-chip-card">
                <span className="fb-chip-ic">
                  <FaCheckCircle />
                </span>
                <div>
                  <p className="fb-chip-title">Book &amp; buy in one place</p>
                  <p className="fb-chip-sub">
                    Services and products, side by side
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust chips */}
        <section className="fb-wrap fb-trust" aria-label="Platform highlights">
          {trust.map((t) => (
            <span key={t}>
              <FaCheckCircle /> {t}
            </span>
          ))}
        </section>

        {/* Client journey */}
        <section className="fb-sec fb-sec--white">
          <div className="fb-wrap">
            <div className="fb-sec-head">
              <p className="fb-eyebrow">For clients</p>
              <h2 className="fb-display">
                From first search to seated, in four steps
              </h2>
            </div>
            <div className="hiw-steps">
              {steps.map((s) => (
                <article className="fb-card hiw-step" key={s.n}>
                  <span className="hiw-num">{s.n}</span>
                  <span className="fb-ic">{s.icon}</span>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Book a service, a package, or buy a product */}
        <section className="fb-sec">
          <div className="fb-wrap">
            <div className="fb-sec-head fb-center">
              <p className="fb-eyebrow">
                Book a service, book a package, or buy a product
              </p>
              <h2 className="fb-display">
                Three ways to shop, one trusted platform
              </h2>
            </div>
            <div className="fb-compare fb-compare--three">
              <article className="fb-panel fb-panel--navy">
                <h3>💆 Book a service</h3>
                <ul>
                  {serviceChecks.map((c) => (
                    <li key={c}>
                      <FaCheckCircle /> {c}
                    </li>
                  ))}
                </ul>
                <Link className="fb-btn fb-btn-light" to="/shops">
                  Find services
                </Link>
              </article>

              <article className="fb-panel fb-panel--cream">
                <h3>🎟️ Book a package</h3>
                <ul>
                  {packageChecks.map((c) => (
                    <li key={c}>
                      <FaCheckCircle /> {c}
                    </li>
                  ))}
                </ul>
                <Link className="fb-panel-link" to="/packages">
                  Browse packages <FaArrowRight />
                </Link>
              </article>

              <article className="fb-panel fb-panel--cream">
                <h3>🛍️ Buy a product</h3>
                <ul>
                  {productChecks.map((c) => (
                    <li key={c}>
                      <FaCheckCircle /> {c}
                    </li>
                  ))}
                </ul>
                <Link className="fb-panel-link" to="/products">
                  Browse products <FaArrowRight />
                </Link>
              </article>
            </div>
          </div>
        </section>

        {/* Built for everyone */}
        <section className="fb-navy">
          <div className="fb-wrap">
            <div className="fb-sec-head">
              <p className="fb-eyebrow">The whole platform</p>
              <h2 className="fb-display">Built for every way you do beauty</h2>
            </div>
            <div className="fb-feat">
              {platformFeatures.map((f) => (
                <div key={f.text}>
                  {f.icon} {f.text}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who it's for */}
        <section className="fb-sec">
          <div className="fb-wrap">
            <div className="fb-sec-head">
              <p className="fb-eyebrow">Who it's for</p>
              <h2 className="fb-display">One platform, three ways in</h2>
            </div>
            <div className="fb-grid">
              {audience.map((a) => (
                <article className="fb-card" key={a.title}>
                  <span className="fb-ic">{a.icon}</span>
                  <h3>{a.title}</h3>
                  <p>{a.desc}</p>
                  <Link className="hiw-card-link" to={a.to}>
                    {a.cta} <FaArrowRight />
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="fb-wrap fb-cta-wrap">
          <div className="fb-cta-band">
            <div>
              <h2 className="fb-display">See it before you sign up</h2>
              <p className="fb-cta-desc">
                Explore a live storefront on the interactive demo — no account
                needed.
              </p>
            </div>
            <div className="fb-cta-actions">
              <a
                className="fb-btn fb-btn-primary"
                href={DEMO_URL}
                target="_blank"
                rel="noreferrer noopener"
              >
                Try the live demo
              </a>
              <a
                className="fb-btn fb-btn-outline"
                href={BOOKING_DEMO_URL}
                target="_blank"
                rel="noreferrer noopener"
              >
                Book a live walkthrough
              </a>
            </div>
          </div>
        </section>
      </main>
      <CustomerFooter />
    </>
  );
}

export default HowItWorks;
